import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './Button'
import { downloadParticipantReport, normaliseLanguage } from '../utils/downloadParticipantReport'
import type { ReportType } from '../utils/reportTypes'

// ── Icon ──────────────────────────────────────────────────────────────────────

const DocumentDownloadIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="12" y1="11" x2="12" y2="17" />
    <polyline points="9 14 12 17 15 14" />
  </svg>
)

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  eventId: string
  reportType: ReportType
  /** Override the button label i18n key. Defaults to `report_generate_pdf`. */
  labelKey?: string
}

/**
 * "Generate report (PDF)" button for participant list tables.
 *
 * Owns loading and error state. Language is taken from the active i18next locale and
 * normalised to 'en' | 'ja' before being forwarded to the backend.
 */
export function GenerateReportButton({
  eventId,
  reportType,
  labelKey = 'report_generate_pdf',
}: Props) {
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleClick() {
    setLoading(true)
    setError(null)
    try {
      await downloadParticipantReport(eventId, reportType, normaliseLanguage(i18n.language))
    } catch {
      setError(t('report_error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<DocumentDownloadIcon />}
        onClick={() => void handleClick()}
        disabled={loading}
        aria-label={t(labelKey)}
      >
        {loading ? t('report_generating') : t(labelKey)}
      </Button>
      {error && (
        <p className="border-pax-red/20 bg-pax-red/5 rounded-lg border px-3 py-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}
